/**
 * Releases — where a feature's audience is decided.
 *
 * The screen is built around the one question a release actually asks: who can see this right
 * now. Stage sets the broad answer; the audience list is the exception for named accounts, which
 * is how a feature reaches a handful of key artists before everyone else.
 */
import { useMemo, useState } from 'react'
import { ChevronRight, Loader2, Plus, ShieldCheck, Trash2, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { userApi } from '@/features/users/api/userApi'
import type { User } from '@/types/entities'
import {
  useAllFeatures,
  useFeatureGrants,
  useGrantFeature,
  useRevokeFeature,
  useSetFeatureStage,
} from '../hooks/useFeatures'
import type { FeatureFlag, FeatureStage } from '../api/flagsApi'

/** What each stage means, in the terms a person deciding a release cares about. */
const STAGES: { value: FeatureStage; label: string; audience: string }[] = [
  { value: 'off', label: 'Off', audience: 'Nobody, including you' },
  { value: 'internal', label: 'Internal', audience: 'Managers, plus anyone below' },
  { value: 'preview', label: 'Preview', audience: 'Managers and the accounts below' },
  { value: 'ga', label: 'Generally available', audience: 'Every artist' },
]

const STAGE_TONE: Record<FeatureStage, string> = {
  off: 'bg-muted text-muted-foreground',
  internal: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  preview: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
  ga: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
}

function personName(g: { firstName: string | null; lastName: string | null; artisticName: string | null; email: string }) {
  return g.artisticName || [g.firstName, g.lastName].filter(Boolean).join(' ') || g.email
}

function AudienceDialog({ flag, onClose }: { flag: FeatureFlag; onClose: () => void }) {
  const { data: grants, isLoading } = useFeatureGrants(flag.key)
  const grantFeature = useGrantFeature()
  const revokeFeature = useRevokeFeature()

  const [search, setSearch] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [searching, setSearching] = useState(false)

  const runSearch = async (term: string) => {
    setSearch(term)
    if (term.trim().length < 2) {
      setResults([])
      return
    }
    setSearching(true)
    try {
      const page = await userApi.listUsers({ search: term.trim(), limit: 8 })
      setResults(page.data)
    } finally {
      setSearching(false)
    }
  }

  const alreadyIn = useMemo(() => new Set((grants ?? []).map((g) => g.userId)), [grants])

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Early access to {flag.label}</DialogTitle>
          <DialogDescription>
            These accounts see {flag.label} regardless of its stage — unless the stage is Off,
            which admits nobody.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="grant-search">Add an artist</Label>
          <Input
            id="grant-search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => runSearch(e.target.value)}
          />
          {searching && <p className="text-xs text-muted-foreground">Searching…</p>}
          {results.length > 0 && (
            <div className="rounded-md border divide-y max-h-56 overflow-y-auto">
              {results.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-3 p-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {u.artisticName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={alreadyIn.has(u.id) || grantFeature.isPending}
                    onClick={() => grantFeature.mutate({ key: flag.key, userId: u.id })}
                  >
                    {alreadyIn.has(u.id) ? 'Already in' : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">
            {isLoading ? 'Loading…' : `${grants?.length ?? 0} account${grants?.length === 1 ? '' : 's'} with early access`}
          </p>
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : (grants ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nobody yet. At Preview that means only managers can see it.
            </p>
          ) : (
            <div className="rounded-md border divide-y max-h-56 overflow-y-auto">
              {(grants ?? []).map((g) => (
                <div key={g.userId} className="flex items-center justify-between gap-3 p-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{personName(g)}</p>
                    <p className="text-xs text-muted-foreground truncate">{g.email}</p>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    title="Remove early access"
                    onClick={() => revokeFeature.mutate({ key: flag.key, userId: g.userId })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function FeatureFlagsPage() {
  const { data: flags, isLoading } = useAllFeatures()
  const setStage = useSetFeatureStage()
  const [audienceFor, setAudienceFor] = useState<FeatureFlag | null>(null)

  return (
    <div className="space-y-8 p-8">
      <div className="space-y-1">
        <h1 className="font-serif text-4xl font-semibold tracking-tight">Releases</h1>
        <p className="text-muted-foreground text-base">
          Who can see each feature right now. Changes reach an artist on their next page load.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {STAGES.map((s) => (
          <Card key={s.value}>
            <CardContent className="p-4">
              <Badge className={cn('mb-2', STAGE_TONE[s.value])} variant="secondary">{s.label}</Badge>
              <p className="text-sm text-muted-foreground">{s.audience}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Features
          </CardTitle>
          <CardDescription>
            {isLoading ? 'Loading…' : `${flags?.length ?? 0} registered`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            [0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)
          ) : (flags ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No features registered yet. They are created by migration, not from this screen — a
              flag has to exist in code before it means anything here.
            </p>
          ) : (
            (flags ?? []).map((f) => (
              <div key={f.key} className="rounded-lg border p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{f.label}</p>
                    <code className="text-xs text-muted-foreground font-mono">{f.key}</code>
                  </div>
                  {f.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">{f.description}</p>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAudienceFor(f)}
                  title="Accounts with early access"
                >
                  <Users className="h-4 w-4 mr-2" />
                  {f.grantCount} early
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>

                <Select
                  value={f.stage}
                  onValueChange={(stage) => setStage.mutate({ key: f.key, stage: stage as FeatureStage })}
                >
                  <SelectTrigger className="w-52">
                    {setStage.isPending && setStage.variables?.key === f.key ? (
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
                      </span>
                    ) : (
                      <SelectValue />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {audienceFor && <AudienceDialog flag={audienceFor} onClose={() => setAudienceFor(null)} />}
    </div>
  )
}
